import get from 'lodash/get';

const interpolateString = (value: string, query: Record<string, any>) => {
  const regex = new RegExp(
    Object.keys(query)
      .map((key) => `{{(${key})}}`)
      .join('|'),
    'g',
  );
  return value.replace(regex, (match) => {
    const parsedKey = match.replace(/{{(.*?)}}/g, '$1');
    return query[parsedKey];
  });
};

type Primitive = string | number | null;

export const interpolate = <T extends Primitive | Array<Primitive> | Record<string, Primitive>>(
  value: T,
  query: Record<string, any> | undefined | null,
): T => {
  if (!query || !value) {
    return value;
  }
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    return interpolateString(value, query) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => interpolate(item, query)) as T;
  }
  if (typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, objectValue]) => {
      // @ts-expect-error types
      acc[key] = interpolate(objectValue, query);
      return acc;
    }, {}) as T;
  }
  return value;
};

type Query = Record<string, string | number | undefined | null | boolean> | undefined;

export const createTranslation = (namespace: any) => {
  function translate(key: string): string;
  function translate(key: string, query: Exclude<Query, undefined>): string;
  function translate(
    key: string,
    query: Query,
    options: { default?: string; returnObjects?: false },
  ): string;
  function translate(
    key: string,
    query: Query,
    options: { default?: never; returnObjects: true },
  ): unknown;
  function translate<T>(
    key: string,
    query: Query,
    options: { default?: undefined; returnObjects: true },
  ): T | undefined;
  function translate<T>(
    key: string,
    query: Query,
    options: { default?: never; returnObjects: true },
  ): T | string;
  function translate<T>(
    key: string,
    query: Query,
    options: { default: T; returnObjects: T extends string ? false : true },
  ): T;

  function translate(key: any, query?: any, options: any = {}) {
    const value = get(namespace, key);
    if (value === undefined || (typeof value !== 'string' && !options.returnObjects)) {
      return 'default' in options ? interpolate(options.default!, query) : key;
    }
    return interpolate(value, query);
  }

  return translate;
};
